/**
 * Etiket modülü için hafif süre ölçümü.
 *
 * Yavaşlık şikayetlerinde hangi adımın ne kadar sürdüğünü kullanıcının
 * konsolundan okuyabilmek için var. Varsayılan olarak kapalı; açmak için
 * tarayıcı konsolunda:
 *
 *   localStorage.setItem('etiketPerf', '1')   // aç
 *   localStorage.removeItem('etiketPerf')     // kapat
 *
 * Ölçüm kapalıyken hiçbir maliyeti yok (tek bayrak okuması).
 */

let enabled: boolean | null = null;

function isEnabled(): boolean {
  if (enabled === null) {
    try {
      enabled = localStorage.getItem('etiketPerf') === '1';
    } catch {
      enabled = false;
    }
  }
  return enabled;
}

/** Bayrağı yeniden okur (konsoldan açıldıktan sonra sayfa yenilemeye gerek kalmasın) */
export function refreshPerfFlag(): void {
  enabled = null;
}

/**
 * Bir işi ölçer ve süresini konsola yazar.
 * Ölçüm kapalıysa işi doğrudan çalıştırır.
 */
export function perfMeasure<T>(label: string, fn: () => T): T {
  if (!isEnabled()) return fn();
  const t0 = performance.now();
  const result = fn();
  // eslint-disable-next-line no-console
  console.log(`[ETIKET-PERF] ${label}: ${(performance.now() - t0).toFixed(1)} ms`);
  return result;
}

/** Asenkron iş için ölçüm */
export async function perfMeasureAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
  if (!isEnabled()) return fn();
  const t0 = performance.now();
  try {
    return await fn();
  } finally {
    // eslint-disable-next-line no-console
    console.log(`[ETIKET-PERF] ${label}: ${(performance.now() - t0).toFixed(1)} ms`);
  }
}

/** Elle bilgi satırı (ör. sayfa/etiket sayısı) */
export function perfLog(message: string): void {
  if (!isEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[ETIKET-PERF] ${message}`);
}

/**
 * Bir kod bloğunun tekrar tekrar çağrılan sürelerini toplayıp özet verir.
 * PDF'te sayfa başına süreleri tek tek basmak konsolu boğuyor.
 */
export function createPerfAccumulator(label: string) {
  let count = 0;
  let totalMs = 0;
  let maxMs = 0;

  const record = (t0: number): void => {
    const ms = performance.now() - t0;
    count++;
    totalMs += ms;
    if (ms > maxMs) maxMs = ms;
  };

  return {
    async run<T>(fn: () => Promise<T>): Promise<T> {
      if (!isEnabled()) return fn();
      const t0 = performance.now();
      try {
        return await fn();
      } finally {
        record(t0);
      }
    },
    runSync<T>(fn: () => T): T {
      if (!isEnabled()) return fn();
      const t0 = performance.now();
      try {
        return fn();
      } finally {
        record(t0);
      }
    },
    summary(): void {
      if (!isEnabled() || count === 0) return;
      // eslint-disable-next-line no-console
      console.log(
        `[ETIKET-PERF] ${label}: ${count} adet, toplam ${totalMs.toFixed(0)} ms, ` +
          `ortalama ${(totalMs / count).toFixed(1)} ms, en yavaş ${maxMs.toFixed(1)} ms`,
      );
    },
  };
}
